import { useState, useEffect } from 'react';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious 
} from "@/components/ui/carousel";
import { Quote } from 'lucide-react';

const Testimonials = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('testimonials');
      if (element) {
        const position = element.getBoundingClientRect();
        if (position.top < window.innerHeight * 0.75) {
          setIsVisible(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const testimonials = [
    {
      id: 1,
      title: "A Nursing Home Without Walls",
      name: "Jacob M.",
      role: "Son of FlowCare Client",
      quote: "FlowCare created what my father calls 'a nursing home without walls.' He gets professional care in his own home, on his schedule, and maintains his independence. The peace of mind is priceless.",
      image: "/lovable-uploads/d52a2fd7-9cfa-4baa-8f78-aada81770d80.png"
    },
    {
      id: 2,
      title: "Recovery Made Possible",
      name: "Sarah T.",
      role: "Recovery Patient",
      quote: "After my surgery, I needed help but didn't want to go to a rehab facility. FlowCare's flexible visits made it possible to recover at home. They came exactly when I needed them, even if just for 30 minutes.",
      image: "/lovable-uploads/81278c91-68fb-45a5-82a2-a020504baeac.png"
    },
    {
      id: 3,
      title: "Staying in Our Home",
      name: "Robert & Diane",
      role: "FlowCare Clients",
      quote: "We've been married 52 years and wanted to stay in our home. Traditional agencies wanted us to pay for 4-hour blocks, which we couldn't afford. FlowCare lets us pay only for what we need, making aging in place financially possible.",
      image: "/lovable-uploads/d210cb87-a4b0-4db8-86ec-f573190f8ef0.png"
    },
    {
      id: 4,
      title: "Cross-Country Peace of Mind",
      name: "Lisa K.",
      role: "Daughter of FlowCare Client",
      quote: "Living 2,000 miles from my mother was a constant worry until we found FlowCare. Their app lets me see when caregivers arrive and leave, and I can video chat during visits to feel connected despite the distance.",
      image: "/lovable-uploads/f036c1de-497c-48c8-a594-3635545b4349.png"
    },
    {
      id: 5,
      title: "Active Lifestyle Support",
      name: "Michael & Jean",
      role: "FlowCare Clients",
      quote: "We still enjoy our daily walks and staying active. FlowCare's flexibility means we can get assistance with specific tasks without interrupting our routines. It's the perfect balance of support and independence.",
      image: "/lovable-uploads/74c20cdf-c592-485c-b197-71c5e0875e32.png"
    }
  ];

  return (
    <section id="testimonials" className="py-20 bg-slate-50">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className={`text-3xl md:text-4xl font-bold text-abbella-ebony mb-4 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            Real Stories, Real Impact
          </h2>
          <p className={`text-lg text-slate-600 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            Hear from families whose lives have been transformed by FlowCare
          </p>
        </div>
        
        <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <Carousel className="max-w-5xl mx-auto">
            <CarouselContent>
              {testimonials.map((testimonial) => (
                <CarouselItem key={testimonial.id} className="md:basis-1/2 lg:basis-1/2">
                  <div className="h-full p-1">
                    <div className="bg-white rounded-xl shadow-md overflow-hidden h-full flex flex-col">
                      <div className="relative h-48 md:h-64 overflow-hidden">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 p-6">
                          <h3 className="text-xl md:text-2xl font-bold text-white mb-1">{testimonial.title}</h3>
                        </div>
                      </div>
                      <div className="p-6 flex flex-col flex-grow">
                        <div className="mb-4 text-abbella-aqua">
                          <Quote size={24} />
                        </div>
                        <p className="text-slate-600 italic mb-6 flex-grow">{testimonial.quote}</p>
                        <div>
                          <p className="font-bold text-abbella-ebony">{testimonial.name}</p>
                          <p className="text-sm text-slate-500">{testimonial.role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-center gap-2 mt-8">
              <CarouselPrevious className="relative left-auto right-auto mr-2" />
              <CarouselNext className="relative left-auto right-auto ml-2" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
